import React, { useState } from "react";
import {
  Card,
  BlockStack,
  Text,
  Button,
  Box,
  Badge,
  InlineStack,
  Divider,
  Collapsible,
} from "@shopify/polaris";
import { 
  ChevronDownIcon, 
  ChevronUpIcon, 
  DeleteIcon 
} from "@shopify/polaris-icons";

export function SupportCard({ 
  isReadOnly,
  onContactSupport, 
  onDeleteTicket,
  disabled, 
  dataTestId, 
  tickets = [] 
}: { 
  isReadOnly?: boolean,
  onContactSupport: () => void, 
  onDeleteTicket?: (id: string) => void,
  disabled?: boolean, 
  dataTestId?: string, 
  tickets?: any[] 
}) {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <Card>
      <BlockStack gap="400">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'nowrap' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <BlockStack gap="100">
              <Text as="h2" variant="headingMd">
                Support & Activity
              </Text>
              <Text as="p" variant="bodyMd" tone="subdued">
                Our team is here to help you maximize your revenue recovery.
              </Text>
            </BlockStack>
          </div>
          <div style={{ flexShrink: 0, marginLeft: '16px', marginTop: '-4px' }}>
            <Button
              variant="tertiary"
              onClick={() => setIsExpanded(!isExpanded)}
              icon={isExpanded ? ChevronUpIcon : ChevronDownIcon}
              accessibilityLabel={isExpanded ? "Collapse support section" : "Expand support section"}
            />
          </div>
        </div>

        <Collapsible
          open={isExpanded}
          id="support-card-collapsible"
          transition={{ duration: '500ms', timingFunction: 'ease-in-out' }}
        >
          <BlockStack gap="400">
            <Box paddingBlockStart="200">
              <div data-testid={dataTestId} className="support-btn" style={{ pointerEvents: isReadOnly ? 'none' : 'auto' }}>
                <Button 
                  onClick={() => {
                    if (isReadOnly) {
                      const shopify = (window as any).shopify;
                      shopify?.toast.show("Support request simulated! Our team usually responds within 2 hours.", { duration: 3000 });
                      return;
                    }
                    onContactSupport();
                  }} 
                  variant="primary" 
                  size="large"
                  fullWidth
                  disabled={false} 
                >
                  Contact Support
                </Button>
              </div>
            </Box>

            {tickets.length > 0 && (
              <BlockStack gap="300">
                <Divider />
                <Text as="h3" variant="headingSm">Recent Tickets</Text>
                <BlockStack gap="200">
                  <div data-testid="recent-tickets-feed">
                    <BlockStack gap="200">
                      {tickets.map((ticket: any) => (
                    <Box 
                      key={ticket.id} 
                      padding="300" 
                      background="bg-surface-secondary" 
                      borderRadius="200"
                      data-testid={`support-ticket-row-${ticket.id}`}
                    >
                      <InlineStack align="space-between" blockAlign="center">
                        <BlockStack gap="100">
                          <Text fontWeight="bold" variant="bodyMd" as="span">{ticket.topic} (#{ticket.id.split('-')[0]})</Text>
                          {ticket.message && (
                            <div style={{ paddingBottom: '4px', maxWidth: '300px' }}>
                              <Text variant="bodySm" tone="subdued" as="p">
                                {ticket.message}
                              </Text>
                            </div>
                          )}
                          <InlineStack gap="200" blockAlign="center">
                            <Badge tone={
                              ticket.status === 'Sent' || ticket.status === 'Open' ? 'info' : 
                              ticket.status === 'Resolved' || ticket.status === 'Closed' ? 'success' : 'attention'
                            }>
                              {ticket.status}
                            </Badge>
                            <Text tone="subdued" variant="bodySm" as="span">{ticket.date}</Text>
                          </InlineStack>
                        </BlockStack>
                          <div data-testid={`delete-ticket-btn-${ticket.id}`} style={{ pointerEvents: isReadOnly ? 'none' : 'auto' }}>
                            <Button 
                              icon={DeleteIcon} 
                              tone="critical" 
                              variant="tertiary"
                              onClick={() => {
                                if (isReadOnly) {
                                  const shopify = (window as any).shopify;
                                  shopify?.toast.show("Ticket deletion simulated.", { duration: 3000 });
                                  return;
                                }
                                onDeleteTicket?.(ticket.id);
                              }}
                              disabled={false}
                              accessibilityLabel={`Delete ticket ${ticket.topic}`}
                            />
                          </div>
                      </InlineStack>
                    </Box>
                  ))}
                    </BlockStack>
                  </div>
                </BlockStack>
              </BlockStack>
            )}
          </BlockStack>
        </Collapsible>
      </BlockStack>
    </Card>
  );
}
